<template>
  <div class="cw-wizard">
    <div v-if="loaded">
      <validation-observer ref="refFormObserver">

        <!-- Step progress bar -->
        <div class="cw-steps">
          <div
            v-for="(step, idx) in steps"
            :key="idx"
            class="cw-step"
            :class="{ 'cw-step--active': currentStep === idx, 'cw-step--done': currentStep > idx }"
            @click="currentStep = idx"
          >
            <div class="cw-step__circle">
              <feather-icon v-if="currentStep > idx" icon="CheckIcon" size="16"/>
              <span v-else>{{ idx + 1 }}</span>
            </div>
            <span class="cw-step__label">{{ step.label }}</span>
            <div v-if="idx < steps.length - 1" class="cw-step__line"></div>
          </div>
        </div>

        <!-- ===== STEP 1: Booking Settings ===== -->
        <transition name="cw-fade" mode="out-in">
          <div v-if="currentStep === 0" key="step-0" class="cw-card">
            <div class="cw-card__header">
              <div class="cw-card__icon"><feather-icon icon="SettingsIcon" size="20"/></div>
              <div>
                <h5 class="cw-card__title">{{ msg('Booking Settings') }}</h5>
                <p class="cw-card__desc">Configure the contract options before continuing.</p>
              </div>
            </div>

            <div class="cw-sync-notice">
              <feather-icon icon="RefreshCwIcon" size="15"/>
              <span>This contract will be automatically synced with the connected channel manager.</span>
            </div>

            <div class="cw-toggles">
              <div
                class="cw-toggle-card"
                :class="{ 'cw-toggle-card--on': contract.isNewClient }"
                @click="contract.isNewClient = !contract.isNewClient"
              >
                <div class="cw-toggle-card__info">
                  <feather-icon icon="UserPlusIcon" size="18" class="cw-toggle-card__ico"/>
                  <div>
                    <span class="cw-toggle-card__name">{{ msg('newclients') }}</span>
                    <span class="cw-toggle-card__hint">Create a new guest profile</span>
                  </div>
                </div>
                <feather-icon v-if="contract.isNewClient" icon="CheckCircleIcon" size="18" class="cw-toggle-card__check"/>
              </div>

              <div
                class="cw-toggle-card"
                :class="{ 'cw-toggle-card--on': contract.paid }"
                @click="contract.paid = !contract.paid"
              >
                <div class="cw-toggle-card__info">
                  <feather-icon icon="CreditCardIcon" size="18" class="cw-toggle-card__ico"/>
                  <div>
                    <span class="cw-toggle-card__name">{{ msg('Paid') }}</span>
                    <span class="cw-toggle-card__hint">Mark as already paid</span>
                  </div>
                </div>
                <feather-icon v-if="contract.paid" icon="CheckCircleIcon" size="18" class="cw-toggle-card__check"/>
              </div>

              <div
                class="cw-toggle-card"
                :class="{ 'cw-toggle-card--on': contract.over30Days }"
                @click="contract.over30Days = !contract.over30Days"
              >
                <div class="cw-toggle-card__info">
                  <feather-icon icon="CalendarIcon" size="18" class="cw-toggle-card__ico"/>
                  <div>
                    <span class="cw-toggle-card__name">{{ msg('Over 30 days') }}</span>
                    <span class="cw-toggle-card__hint">Extended stay booking</span>
                  </div>
                </div>
                <feather-icon v-if="contract.over30Days" icon="CheckCircleIcon" size="18" class="cw-toggle-card__check"/>
              </div>
            </div>

            <!-- Over 30 days platform picker -->
            <transition name="cw-fade">
              <div v-if="contract.over30Days" class="cw-platforms">
                <p class="cw-platforms__label">Select extended-stay platform</p>
                <b-form-radio-group
                  v-model="contract.platform30DaysId"
                  name="extendedStayPlatforms"
                  class="cw-platforms__grid"
                >
                  <label
                    v-for="option in options"
                    :key="option.value"
                    class="cw-platform"
                    :class="{ 'cw-platform--active': contract.platform30DaysId === option.value }"
                  >
                    <b-form-radio :value="option.value" class="d-none"/>
                    <img :src="option.image" :alt="option.text" class="cw-platform__img"/>
                    <span class="cw-platform__name">{{ option.text }}</span>
                    <div class="cw-platform__check"><feather-icon icon="CheckIcon" size="14"/></div>
                  </label>
                </b-form-radio-group>
              </div>
            </transition>
          </div>

          <!-- ===== STEP 2: Guest Information ===== -->
          <div v-else-if="currentStep === 1" key="step-1" class="cw-card">
            <div class="cw-card__header">
              <div class="cw-card__icon"><feather-icon icon="UsersIcon" size="20"/></div>
              <div>
                <h5 class="cw-card__title">{{ msg('Guest Information') }}</h5>
                <p class="cw-card__desc">{{ contract.isNewClient ? 'Fill in the new guest details.' : 'Search and select an existing client.' }}</p>
              </div>
            </div>

            <template v-if="!contract.isNewClient">
              <b-row>
                <b-col cols="12" md="8" lg="6">
                  <b-form-group :label="msg('Client Id')" label-for="realEstate-name">
                    <validation-provider #default="{ errors }" :name="msg('Client')" rules="required">
                      <client-picker v-model="contract.clientId"/>
                      <small class="text-danger">{{ msg(errors[0]) }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </template>

            <template v-else>
              <b-row>
                <b-col cols="12" md="6">
                  <b-form-group :label="msg('Firstname')">
                    <validation-provider #default="{ errors }" :name="msg('Client')" rules="required">
                      <b-form-input v-model="contract.client.firstname" placeholder="John"/>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group :label="msg('Lastname')">
                    <validation-provider #default="{ errors }" :name="msg('Lastname')" rules="required">
                      <b-form-input v-model="contract.client.lastname" placeholder="Doe"/>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group :label="msg('Phone')">
                    <validation-provider #default="{ errors }" :name="msg('Phone')" rules="required">
                      <b-form-input v-model="contract.client.phone" placeholder="+43 ..."/>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group :label="msg('Email')">
                    <validation-provider #default="{ errors }" :name="msg('Email')" rules="required">
                      <b-form-input v-model="contract.client.email" placeholder="guest@email.com"/>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <hr class="cw-divider"/>

              <b-row>
                <b-col cols="12" md="5">
                  <b-form-group :label="msg('Address')">
                    <validation-provider #default="{ errors }" :name="msg('Address')" rules="required">
                      <b-form-input v-model="contract.client.address.street"/>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="6" md="2">
                  <b-form-group :label="msg('Zip')">
                    <validation-provider #default="{ errors }" :name="msg('Zip')" rules="required">
                      <b-form-input v-model="contract.client.address.zip"/>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="6" md="2">
                  <b-form-group :label="msg('City')">
                    <validation-provider #default="{ errors }" :name="msg('City')" rules="required">
                      <b-form-input v-model="contract.client.address.city"/>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="3">
                  <b-form-group :label="msg('Country')" label-for="client-country">
                    <validation-provider #default="{ errors }" :name="msg('Country')" rules="required">
                      <v-select
                        v-bind:dir="isRTL ? 'rtl' : 'ltr'"
                        v-model="contract.client.address.country"
                        :options="allCountries"
                        class="w-100"
                        label="name"
                        :reduce="(lang) => lang.iso"
                        autocomplete="on"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12" md="3">
                  <b-form-group :label="msg('Number of guests')">
                    <validation-provider #default="{ errors }" :name="msg('Number of guests')" rules="required">
                      <b-form-input id="realEstate-sell-price" type="number" v-model="contract.numberOfGuests"/>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </template>
          </div>

          <!-- ===== STEP 3: Property & Dates ===== -->
          <div v-else-if="currentStep === 2" key="step-2" class="cw-card">
            <div class="cw-card__header">
              <div class="cw-card__icon"><feather-icon icon="HomeIcon" size="20"/></div>
              <div>
                <h5 class="cw-card__title">{{ msg('Property & Dates') }}</h5>
                <p class="cw-card__desc">Choose properties, set the stay period, and cancellation policy.</p>
              </div>
            </div>

            <b-row>
              <b-col cols="12">
                <b-form-group :label="msg('Real estates')">
                  <real-estate-picker multiple v-model="contract.propertyIds"/>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12" md="6">
                <b-form-group :label="msg('Start')">
                  <validation-provider #default="{ errors }" :name="msg('Start date')" rules="required">
                    <calendar-picker v-model="contract.bnbStyleRent.start"/>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group :label="msg('End')">
                  <validation-provider #default="{ errors }" :name="msg('End date')" rules="required">
                    <calendar-picker v-model="contract.bnbStyleRent.end"/>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <hr class="cw-divider"/>

            <b-row>
              <b-col cols="12">
                <b-form-group :label="msg('Cancellation options')">
                  <v-select
                    v-model="cancelationOptionPicked"
                    :options="cancellationOptions"
                    label="name"
                    :clearable="false"
                    :placeholder="msg('Select a cancellation option')"
                    :reduce="option => option.id"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <!-- ===== STEP 4: Confirm & Book ===== -->
          <div v-else-if="currentStep === 3" key="step-3" class="cw-card">
            <div class="cw-card__header">
              <div class="cw-card__icon cw-card__icon--confirm"><feather-icon icon="CheckSquareIcon" size="20"/></div>
              <div>
                <h5 class="cw-card__title">{{ msg('Confirm & Book') }}</h5>
                <p class="cw-card__desc">Set the pricing and finalize the reservation.</p>
              </div>
            </div>

            <!-- Pricing -->
            <b-row v-if="contract.propertyIds != null && contract.propertyIds.length > 0">
              <b-col cols="12" md="6" v-for="(address, id) in realEstatesIdAddressMap" :key="id">
                <div class="cw-price-card">
                  <span class="cw-price-card__address">{{ address }}</span>
                  <validation-provider #default="{ errors }" :name="`${msg('Price for')} ${address}`" rules="required">
                    <b-form-input :id="`realEstate-sell-price-${id}`" type="number" step="any" v-model="contract.prices[id]" class="cw-price-card__input" placeholder="0.00"/>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </div>
              </b-col>
            </b-row>

            <div v-else class="cw-empty-state">
              <feather-icon icon="AlertCircleIcon" size="36"/>
              <p>Please go back and select at least one property.</p>
            </div>

            <!-- Booking summary -->
            <div class="cw-summary" v-if="contract.propertyIds && contract.propertyIds.length > 0">
              <div class="cw-summary__row" v-if="contract.bnbStyleRent.start && contract.bnbStyleRent.end">
                <feather-icon icon="CalendarIcon" size="14"/>
                <span>{{ contract.bnbStyleRent.start }} &mdash; {{ contract.bnbStyleRent.end }}</span>
              </div>
              <div class="cw-summary__row">
                <feather-icon icon="HomeIcon" size="14"/>
                <span>{{ contract.propertyIds.length }} {{ contract.propertyIds.length === 1 ? 'property' : 'properties' }}</span>
              </div>
              <div class="cw-summary__row" v-if="contract.isNewClient && contract.client.firstname">
                <feather-icon icon="UserIcon" size="14"/>
                <span>{{ contract.client.firstname }} {{ contract.client.lastname || '' }}</span>
              </div>
              <div class="cw-summary__row" v-if="contract.paid">
                <feather-icon icon="CheckCircleIcon" size="14"/>
                <span>Marked as paid</span>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="cw-actions">
              <b-button variant="outline-warning" class="cw-actions__btn" @click="onCheckAvailability">
                <feather-icon icon="CalendarIcon" size="16" class="mr-50"/>
                {{ msg('Check Availability') }}
              </b-button>
              <b-button :disabled="submitting" variant="success" class="cw-actions__btn cw-actions__btn--book" @click="onBookNow">
                <b-spinner v-if="submitting" small class="mr-50"/>
                <feather-icon v-else icon="SendIcon" size="16" class="mr-50"/>
                {{ submitting ? msg('Booking...') : msg('Book Now') }}
              </b-button>
            </div>
            <transition name="cw-fade">
              <button v-if="submitting" class="cw-reset-link" @click="onReset">
                <feather-icon icon="RotateCcwIcon" size="13"/>
                Taking too long? Click to reset
              </button>
            </transition>
          </div>
        </transition>

        <!-- Step navigation (hidden on last step — actions are built in) -->
        <div class="cw-nav" v-if="currentStep < steps.length - 1">
          <b-button v-if="currentStep > 0" variant="outline-secondary" class="cw-nav__btn" @click="currentStep--">
            <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50"/>
            {{ msg('Back') }}
          </b-button>
          <span v-else></span>
          <b-button variant="primary" class="cw-nav__btn cw-nav__btn--next" @click="currentStep++">
            {{ msg('Next') }}
            <feather-icon icon="ArrowRightIcon" size="16" class="ml-50"/>
          </b-button>
        </div>
        <div class="cw-nav" v-else>
          <b-button variant="outline-secondary" class="cw-nav__btn" @click="currentStep--">
            <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50"/>
            {{ msg('Back') }}
          </b-button>
          <span></span>
        </div>

      </validation-observer>
    </div>

    <div v-else-if="this.error != null">
      <b-alert show variant="danger"><i class="fas fa-exclamation-triangle mr-3"></i>
        <strong>{{ error }}</strong>
      </b-alert>
    </div>

    <div v-else class="cw-loading">
      <b-spinner label="Spinning" variant="primary"/>
      <span>{{ msg('loading') }}</span>
    </div>
  </div>
</template>

<script>

import {mapActions, mapGetters} from 'vuex'
import ClientPicker from "@/components/components/picker/clientPicker";
import RealEstatePicker from "@/components/components/picker/realEstatesPicker";

export default {
  name: 'treasureContractRentPanelMulti',
  components: {RealEstatePicker, ClientPicker},
  data() {
    return {
      currentStep: 0,
      steps: [
        { label: 'Settings', icon: 'SettingsIcon' },
        { label: 'Guest', icon: 'UsersIcon' },
        { label: 'Property', icon: 'HomeIcon' },
        { label: 'Confirm', icon: 'CheckSquareIcon' },
      ],
      submitting: false,
      loaded: false,
      pricelabPrices: [],
      error: null,
      selectedOption: null,
      firstProperty: null,
      options: [
        {
          value: "Housing anywhere",
          text: "Housing anywhere",
          image: require('@/assets/images/platforms/housing.jpg'),
        },
        {
          value: "Homelike",
          text: "Homelike",
          image: require('@/assets/images/platforms/homelike.jpg'),
        },
        {
          value: "Wunderflats",
          text: "Wunderflats",
          image: require('@/assets/images/platforms/wunder.jpg'),
        },
        {
          value: "Treasure home",
          text: "Treasure home",
          image: "https://treasurehome.at/uploads/images/tLogo.png",
        }
      ],

      contractId: 0,
      isPricelabs: false,
      cancelationOptionPicked: null,
      cancellationOptions: [],

      contract: {
        prices: {},
        propertyIds: [],
        hostawayApi: true,
        isNewClient: true,
        paid: false,
        over30Days: false,
        platform30DaysId: null,
        name: '',
        client: {
          firstname: null,
          email: null,
          phone: null,
          lastname: null,
          address: {
            street: null,
            zip: null,
            city: null,
            country: null
          },
        },

        bnbStyleRent: {
          platform: null,
          active: true,
          start: null,
          end: null,
          totalPayment: 0.0,
          oneTimePayment: false,
          totalPaymentDiscount: null,
          totalServiceFee: null,
          totalPriceHandlingFee: null,
        },
        type: null,
      },
      languageOptions: [
        // Define your language options here, excluding Arabic
        {id: 'EN', name: 'English'},
        {id: 'ES', name: 'Spanish'},
        {id: 'FR', name: 'French'},
        {id: 'DE', name: 'German'},
        {id: 'IT', name: 'Italian'},
        {id: 'PT', name: 'Portuguese'},
        {id: 'RU', name: 'Russian'},
        {id: 'CN', name: 'Chinese'},
        {id: 'JP', name: 'Japanese'}
        // Add more languages as needed
      ],
      Refilter: {
        like: {search: '', name: "", shortDescription: ""},
        eq: {type: "", realEstateArea: {}, featureStatus: null},
        lte: {realEstatePrices: {}},
        gte: {realEstatePrices: {}},
        in: {}
      },
      realEstatesIdAddressMap: {},
      //Listeners
      listeners: {
        submit: () => {
        }
      }
    }
  },
  watch: {
    id: function () { // watch it
      this.onLoad()
      this.error = null
    },
    'contract.propertyIds': {
      handler(newPropertyIds, oldPropertyIds) {
        // This function will be called whenever contract.propertyIds changes
        console.log('propertyIds changed', newPropertyIds, oldPropertyIds);
        this.getProperties()
      },
      deep: true, // Use deep watcher to detect changes within the array
    },
  },
  computed: {
    ...mapGetters('realEstate', ['getAllTypes']),
    ...mapGetters('data', ['getAllCountries', 'getAllLanguage']),

    allCountries() {
      return Object.values(this.getAllCountries)
    },
    propertiesWithZeros() {
      // Check if contract.propertyIds exists and has a length
      if (this.contract && Array.isArray(this.contract.propertyIds) && this.contract.propertyIds.length > 0) {
        // Create an array of the same length, with each element being { value: 0 }
        return this.contract.propertyIds.map(() => ({value: 0}));
      }
      return []; // Return an empty array if contract.propertyIds is not set or empty
    },
  },
  created() {

    this.onLoad()
    this.$root.$on('crm::submitContract', (this.listeners.submit = () => {
      this.submit()
    }))
    this.$root.$on('crm::checkContract', (this.listeners.checkContract = () => {
      this.checkContract()
    }))
  },
  beforeDestroy() {
    this.$root.$off('crm::submitContract', this.listeners.submit)
    this.$root.$off('crm::checkContract', this.listeners.checkContract)
  },
  methods: {
    ...mapActions('realEstateContracts', {
      _createContractMulti: 'createContractMulti', _checkCreateMulti: 'checkCreateMulti'
    }),
    ...mapActions('realEstate', ['getAllRealEstates']),
    ...mapActions('pricelabs', ['calcPrices']),
    setData(propertyIds, start, end) {
      this.contract.propertyIds = propertyIds
      this.contract.bnbStyleRent.start = start
      this.contract.bnbStyleRent.end = end
      this.loaded = true
    },
    getProperties() {
      this.Refilter.in = {id: this.contract.propertyIds}
      this.getAllRealEstates({
        page: 1,
        amount: 9999,
        filter: this.Refilter,
        sortcolumn: 'updatedDate',
        sortby: 'desc',
      }).then(this.onRERetrieve, (error) => this.error = error)
    },
    calculatePricelabsPrice() {
      let $this = this;
      let request = {
        realEstateIds: this.contract.propertyIds,
        start: this.contract.bnbStyleRent.start,
        end: this.contract.bnbStyleRent.end,
      }
      this.calcPrices({body: request}).then((ans) => {

        $this.contract.prices = ans

      }, (error) => this.error = error)
    },

    onRERetrieve(data) {
      this.firstProperty = data["RealEstates"][0]
      this.realEstatesIdAddressMap = data["RealEstates"].reduce((acc, cur) => {
        acc[cur.id] = cur.address + " " + cur.house + "/" + cur.apt + " " + cur.zip
        return acc
      }, {});

      this.loadCancellationOptions();

    },

    onLoad() {
      this.loaded = true

    }
    ,
    loadCancellationOptions() {
      if (this.firstProperty == null) {
        return;
      }
      if (this.firstProperty.realEstateCancel == null) {
        this.cancellationOptions = [
          {id: 'nonRefundable', name: this.msg('cancellationOptions.nonRefundable')} // Static non-refundable option
        ];
        return;
      }

      // Always include the static non-refundable option with translation
      this.cancellationOptions = [
        {id: 'nonRefundable', name: this.msg('cancellationOptions.nonRefundable')}
      ];

      // Dynamic options based on realEstateCancel booleans, with translations
      if (this.firstProperty.realEstateCancel.threeDays) {
        this.cancellationOptions.push({
          id: 'threeDays',
          name: `${this.msg('cancellationOptions.cancelUpTo')} 3 ${this.msg('cancellationOptions.days')} (${this.msg('cancellationOptions.extraFee')}: ${this.firstProperty.realEstateCancel.threeDaysFeePercent}%)`
        });
      }

      if (this.firstProperty.realEstateCancel.sevenDays) {
        this.cancellationOptions.push({
          id: 'sevenDays',
          name: `${this.msg('cancellationOptions.cancelUpTo')} 7 ${this.msg('cancellationOptions.days')} (${this.msg('cancellationOptions.extraFee')}: ${this.firstProperty.realEstateCancel.sevenDaysFeePercent}%)`
        });
      }

      if (this.firstProperty.realEstateCancel.fourteenDays) {
        this.cancellationOptions.push({
          id: 'fourteenDays',
          name: `${this.msg('cancellationOptions.cancelUpTo')} 14 ${this.msg('cancellationOptions.days')} (${this.msg('cancellationOptions.extraFee')}: ${this.firstProperty.realEstateCancel.fourteenDaysFeePercent}%)`
        });
      }
    }

    ,
    onBookNow() {
      this.submitting = true;
      this.createContract();
    },
    onCheckAvailability() {
      this.checkContract();
    },
    onReset() {
      this.submitting = false;
    },
    createContract() {
      let $this = this
      if (this.contract.paid == null)
        this.contract.paid = false

      if (this.contract.over30Days == null)
        this.contract.over30Days = false
      else if (this.contract.over30Days && this.contract.platform30DaysId == null) {
        this.contract.platform30DaysId = "Treasure home"
      }

      this.contract.cancelationOptionPicked = this.cancelationOptionPicked;

      this.$refs['refFormObserver'].validate().then(valid => {
        if (!valid) {
          $this.submitting = false;
          return;
        }
        $this._createContractMulti(this.contract)
            .then(
                (id) => {
                  $this.contractId = id
                  $this.$emit('submitted', {
                    status: true,
                    server: id,
                    id
                  })
                },
                (a) => {
                  $this.submitting = false;
                  $this.$emit('submitted', {
                    status: false,
                    server: a
                  })
                }
            )
            .finally(() => {
              if ($this.contractId != null && $this.contractId !== 0 && $this.contractId !== 'success')
                this.$router.push(`/real_estate/contracts/rent/${$this.contractId}`);
            })
      })
    },

    validate(field) {
      return this.$v.contract[field] != null && this.$v.contract[field].$error === true
    },
    submit() {
      return this.createContract()
    },
    checkContract() {
      let $this = this;
      this.$refs['refFormObserver'].validate().then(valid => {
        if (!valid)
          return;
        $this._checkCreateMulti(this.contract)
            .then(
                (id) => {
                  $this.contractId = id
                  $this.$emit('checked', {
                    status: true,
                    server: id,
                    id
                  })
                },
                (a) => $this.$emit('checked', {
                  status: false,
                  server: a
                })
            )
      })
    }

  }


}
</script>

<style scoped lang="scss">
$accent: #7367f0;
$accent-light: rgba(115, 103, 240, 0.08);
$success: #28c76f;
$border: #ebe9f1;
$text-muted: #a8a3b8;
$card-bg: #fff;
$radius: 12px;

.cw-wizard {
  padding: 4px 0 0;
}

/* ---- Step progress bar ---- */
.cw-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 28px;
  padding: 0 8px;
}

.cw-step {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s;

  &:hover { opacity: 0.85; }
}

.cw-step__circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  background: $accent-light;
  color: $accent;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.cw-step--active .cw-step__circle {
  background: $accent;
  color: #fff;
  box-shadow: 0 4px 14px rgba(115, 103, 240, 0.35);
  border-color: $accent;
}

.cw-step--done .cw-step__circle {
  background: $success;
  color: #fff;
  border-color: $success;
}

.cw-step__label {
  font-size: 12px;
  font-weight: 600;
  color: $text-muted;
  margin-left: 8px;
  white-space: nowrap;
  transition: color 0.2s;
}

.cw-step--active .cw-step__label { color: $accent; }
.cw-step--done .cw-step__label { color: $success; }

.cw-step__line {
  width: 40px;
  height: 2px;
  background: $border;
  margin: 0 12px;
  border-radius: 2px;
  transition: background 0.3s;
}

.cw-step--done + .cw-step .cw-step__line,
.cw-step--done .cw-step__line {
  background: $success;
}

/* ---- Card sections ---- */
.cw-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  padding: 20px;
  min-height: 240px;
}

.cw-card__header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 24px;
}

.cw-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: $accent-light;
  color: $accent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cw-card__title {
  margin: 0 0 2px;
  font-size: 16px;
  font-weight: 700;
  color: #2d2b3a;
}

.cw-card__desc {
  margin: 0;
  font-size: 13px;
  color: $text-muted;
}

.cw-divider {
  border: none;
  border-top: 1px dashed $border;
  margin: 12px 0 18px;
}

/* ---- Toggle cards (step 1) ---- */
.cw-sync-notice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: rgba(40, 199, 111, 0.08);
  border: 1px solid rgba(40, 199, 111, 0.2);
  border-radius: 8px;
  margin-bottom: 16px;
  color: #28c76f;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}

.cw-toggles {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cw-toggle-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: rgba(234, 84, 85, 0.06);
  border: 1.5px solid rgba(234, 84, 85, 0.18);
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(234, 84, 85, 0.1);
    border-color: rgba(234, 84, 85, 0.3);
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(234, 84, 85, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
}

.cw-toggle-card--on {
  background: rgba(40, 199, 111, 0.08);
  border-color: rgba(40, 199, 111, 0.3);

  &:hover {
    background: rgba(40, 199, 111, 0.12);
    border-color: rgba(40, 199, 111, 0.4);
    box-shadow: 0 3px 10px rgba(40, 199, 111, 0.12);
  }

  .cw-toggle-card__ico {
    color: $success;
    opacity: 1;
  }

  .cw-toggle-card__name {
    color: #1e7a45;
  }
}

.cw-toggle-card__info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.cw-toggle-card__check {
  flex-shrink: 0;
  color: $success;
}

.cw-toggle-card__ico {
  color: rgba(234, 84, 85, 0.5);
  transition: color 0.25s, opacity 0.25s;
  flex-shrink: 0;
}

.cw-toggle-card__name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #2d2b3a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.25s;
}

.cw-toggle-card__hint {
  display: block;
  font-size: 11px;
  color: $text-muted;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ---- Platform cards (over 30 days) ---- */
.cw-platforms {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px dashed $border;
}

.cw-platforms__label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: $text-muted;
  margin-bottom: 12px;
}

.cw-platforms__grid {
  display: grid !important;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.cw-platform {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px;
  border: 2px solid $border;
  border-radius: $radius;
  cursor: pointer;
  transition: all 0.25s ease;
  background: #fff;

  &:hover {
    border-color: rgba(115, 103, 240, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }
}

.cw-platform--active {
  border-color: $accent !important;
  background: $accent-light;
  box-shadow: 0 4px 16px rgba(115, 103, 240, 0.12) !important;
}

.cw-platform__img {
  height: 56px;
  width: 100%;
  object-fit: contain;
  border-radius: 6px;
  margin-bottom: 8px;
}

.cw-platform__name {
  font-size: 12px;
  font-weight: 600;
  color: #2d2b3a;
  text-align: center;
}

.cw-platform__check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: $accent;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.25s ease;
}

.cw-platform--active .cw-platform__check {
  opacity: 1;
  transform: scale(1);
}

/* ---- Confirm icon variant ---- */
.cw-card__icon--confirm {
  background: rgba(40, 199, 111, 0.08);
  color: #28c76f;
}

/* ---- Price cards (step 4) ---- */
.cw-price-card {
  padding: 16px;
  background: #fafaff;
  border: 1px solid $border;
  border-radius: 10px;
  margin-bottom: 14px;
}

.cw-price-card__address {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: $text-muted;
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cw-price-card__input {
  font-size: 18px !important;
  font-weight: 700 !important;
  text-align: center;
}

.cw-btn-pricelabs {
  border-radius: 10px;
  font-weight: 600;
  padding: 10px 20px;
}

.cw-pricelabs-action {
  margin-bottom: 8px;
}

/* ---- Booking summary (step 4) ---- */
.cw-summary {
  margin-top: 20px;
  padding: 14px 16px;
  background: rgba(115, 103, 240, 0.04);
  border: 1px dashed rgba(115, 103, 240, 0.2);
  border-radius: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 24px;
}

.cw-summary__row {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #4b4763;
}

/* ---- Action buttons (step 4) ---- */
.cw-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid $border;
}

.cw-actions__btn {
  border-radius: 10px;
  font-weight: 600;
  padding: 10px 24px;
  display: flex;
  align-items: center;
}

.cw-actions__btn--book {
  box-shadow: 0 4px 16px rgba(40, 199, 111, 0.3);
  min-width: 150px;
  justify-content: center;
}

.cw-reset-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  margin-top: 10px;
  padding: 0;
  background: none;
  border: none;
  font-size: 12px;
  color: $text-muted;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #ea5455;
  }
}

.cw-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: $text-muted;
  text-align: center;
  gap: 12px;

  p {
    margin: 0;
    font-size: 13px;
  }
}

/* ---- Navigation ---- */
.cw-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid $border;
}

.cw-nav__btn {
  border-radius: 10px;
  font-weight: 600;
  padding: 10px 22px;
  display: flex;
  align-items: center;
}

.cw-nav__btn--next {
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.25);
}

/* ---- Loading state ---- */
.cw-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  gap: 14px;
  color: $text-muted;
}

/* ---- Transition ---- */
.cw-fade-enter-active,
.cw-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.cw-fade-enter {
  opacity: 0;
  transform: translateX(12px);
}

.cw-fade-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}

/* ---- Responsive ---- */
@media (max-width: 767px) {
  .cw-step__label { display: none; }
  .cw-step__line { width: 24px; margin: 0 6px; }
  .cw-card { padding: 16px; }
  .cw-platforms__grid { grid-template-columns: repeat(2, 1fr); }
}
</style>

<style lang="scss">
/* Dark layout overrides (unscoped so .dark-layout ancestor selector works) */
.dark-layout .cw-wizard {
  $dark-bg: #283046;
  $dark-card: #283046;
  $dark-card-inner: #1e2a3a;
  $dark-border: rgba(255, 255, 255, 0.08);
  $dark-text: #d0d2d6;
  $dark-text-muted: #7c8ba5;
  $accent: #7367f0;
  $accent-light-dark: rgba(115, 103, 240, 0.15);
  $success: #28c76f;

  /* Step bar */
  .cw-step__circle {
    background: $accent-light-dark;
    color: lighten($accent, 12%);
  }

  .cw-step--active .cw-step__circle {
    background: $accent;
    color: #fff;
  }

  .cw-step--done .cw-step__circle {
    background: $success;
    color: #fff;
  }

  .cw-step__label {
    color: $dark-text-muted;
  }

  .cw-step--active .cw-step__label {
    color: lighten($accent, 12%);
  }

  .cw-step--done .cw-step__label {
    color: $success;
  }

  .cw-step__line {
    background: $dark-border;
  }

  .cw-step--done .cw-step__line {
    background: $success;
  }

  /* Card sections */
  .cw-card {
    background: $dark-card;
    border-color: $dark-border;
  }

  .cw-card__icon {
    background: $accent-light-dark;
    color: lighten($accent, 12%);
  }

  .cw-card__title {
    color: $dark-text;
  }

  .cw-card__desc {
    color: $dark-text-muted;
  }

  .cw-divider {
    border-top-color: $dark-border;
  }

  /* Sync notice */
  .cw-sync-notice {
    background: rgba(40, 199, 111, 0.1);
    border-color: rgba(40, 199, 111, 0.2);
    color: #48da89;
  }

  /* Toggle cards */
  .cw-toggle-card {
    background: rgba(234, 84, 85, 0.08);
    border-color: rgba(234, 84, 85, 0.2);

    &:hover {
      background: rgba(234, 84, 85, 0.12);
      border-color: rgba(234, 84, 85, 0.3);
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
    }
  }

  .cw-toggle-card--on {
    background: rgba(40, 199, 111, 0.1);
    border-color: rgba(40, 199, 111, 0.25);

    &:hover {
      background: rgba(40, 199, 111, 0.15);
      border-color: rgba(40, 199, 111, 0.35);
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
    }

    .cw-toggle-card__name {
      color: #48da89;
    }

    .cw-toggle-card__ico {
      color: #48da89;
    }

    .cw-toggle-card__check {
      color: #48da89;
    }
  }

  .cw-toggle-card__name {
    color: $dark-text;
  }

  .cw-toggle-card__hint {
    color: $dark-text-muted;
  }

  .cw-toggle-card__ico {
    color: rgba(234, 84, 85, 0.5);
  }

  /* Platform cards */
  .cw-platforms {
    border-top-color: $dark-border;
  }

  .cw-platforms__label {
    color: $dark-text-muted;
  }

  .cw-platform {
    background: $dark-card-inner;
    border-color: $dark-border;

    &:hover {
      border-color: rgba(115, 103, 240, 0.35);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    }
  }

  .cw-platform--active {
    background: $accent-light-dark;
    border-color: $accent !important;
  }

  .cw-platform__name {
    color: $dark-text;
  }

  /* Price cards */
  .cw-price-card {
    background: $dark-card-inner;
    border-color: $dark-border;
  }

  .cw-price-card__address {
    color: $dark-text-muted;
  }

  .cw-price-card__input {
    background: rgba(255, 255, 255, 0.05) !important;
    color: $dark-text !important;
    border-color: $dark-border !important;
  }

  /* Confirm icon */
  .cw-card__icon--confirm {
    background: rgba(40, 199, 111, 0.12);
    color: #48da89;
  }

  /* Booking summary */
  .cw-summary {
    background: rgba(115, 103, 240, 0.08);
    border-color: rgba(115, 103, 240, 0.18);
  }

  .cw-summary__row {
    color: $dark-text;
  }

  /* Actions */
  .cw-actions {
    border-top-color: $dark-border;
  }

  .cw-reset-link {
    color: $dark-text-muted;

    &:hover {
      color: #ea5455;
    }
  }

  /* Empty state */
  .cw-empty-state {
    color: $dark-text-muted;
  }

  /* Navigation */
  .cw-nav {
    border-top-color: $dark-border;
  }

  /* Loading */
  .cw-loading {
    color: $dark-text-muted;
  }

  /* Form inputs and labels (Bootstrap-Vue overrides) */
  .form-control {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: $dark-border;
    color: $dark-text;

    &:focus {
      background-color: rgba(255, 255, 255, 0.08);
      border-color: rgba(115, 103, 240, 0.5);
      color: $dark-text;
    }

    &::placeholder {
      color: $dark-text-muted;
    }
  }

  label, .col-form-label {
    color: $dark-text;
  }
}
</style>

